import { useUser } from '@clerk/react';
import { useGetDashboard, useGetSubscription } from '@workspace/api-client-react';
import { StatCard } from '@/components/stat-card';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TierBadge } from '@/components/tier-badge';
import { StatusBadge } from '@/components/status-badge';
import { RecommendationBadge } from '@/components/recommendation-badge';
import { PlatformBadge } from '@/components/platform-badge';
import { ConfidenceBar } from '@/components/ui/confidence-bar';
import { Spinner } from '@/components/ui/spinner';
import { GetStartedBanner } from '@/components/get-started-banner';
import { Link } from 'wouter';
import { Button } from '@/components/ui/button';
import {
  DollarSign,
  TrendingUp,
  Target,
  Briefcase,
  ArrowRight,
  Zap,
} from 'lucide-react';
import { cn } from '@/lib/utils';

export default function DashboardPage() {
  const { user } = useUser();
  const { data: dashboard, isLoading } = useGetDashboard();
  const { data: subscription } = useGetSubscription();

  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="lg" />
      </div>
    );
  }

  const stats = dashboard?.summary || {
    totalPnl: 0,
    winRate: 0,
    aiAccuracyRate: 0,
    openPositions: 0,
  };

  const hasAnalyses = (dashboard?.recentAnalyses?.length ?? 0) > 0;
  const hasPositions = (dashboard?.activePositions?.length ?? 0) > 0 || (stats.openPositions ?? 0) > 0;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8 pb-24 lg:pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">
            {greeting()}, {user?.firstName || 'there'}
          </h1>
          <p className="text-muted-foreground mt-1">
            Here's what's happening with your markets
          </p>
        </div>
        <TierBadge tier={(subscription?.effectiveTier ?? subscription?.tier ?? dashboard?.subscription?.effectiveTier ?? 'free') as 'free' | 'starter' | 'pro'} />
      </div>

      {/* Onboarding — only rendered until dismissed or all steps complete */}
      <GetStartedBanner hasAnalyses={hasAnalyses} hasPositions={hasPositions} />

      {/* Upgrade prompt for free-tier users — hidden for anyone with effective (real or referral-bonus) Pro/Starter access */}
      {(subscription?.effectiveTier ?? subscription?.tier) === 'free' && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 rounded-lg bg-primary/5 border border-primary/10">
          <div className="flex items-start gap-3">
            <Zap className="h-5 w-5 text-primary mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-sm">You're on the Free plan</p>
              <p className="text-sm text-muted-foreground">
                {(subscription?.analysesUsed ?? 0) >= (subscription?.analysesLimit ?? 0)
                  ? "You've used all your analyses this month."
                  : `${(subscription?.analysesLimit ?? 0) - (subscription?.analysesUsed ?? 0)} of ${subscription?.analysesLimit ?? 0} analyses remaining.`}{' '}
                Upgrade for more.
              </p>
            </div>
          </div>
          <Link href="/settings">
            <Button size="sm" className="shrink-0 whitespace-nowrap">
              <Zap className="mr-1.5 h-3.5 w-3.5" />
              Upgrade to Pro
            </Button>
          </Link>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          label="Total P&L"
          value={`$${stats.totalPnl.toFixed(2)}`}
          icon={DollarSign}
          trend={stats.totalPnl >= 0 ? 'up' : 'down'}
        />
        <StatCard
          label="Win Rate"
          value={`${Math.round((stats.winRate ?? 0) * 100)}%`}
          icon={Target}
          trend={(stats.winRate ?? 0) >= 0.5 ? 'up' : 'down'}
        />
        <StatCard
          label="AI Accuracy"
          value={`${Math.round((stats.aiAccuracyRate ?? 0) * 100)}%`}
          icon={TrendingUp}
          trend="neutral"
        />
        <StatCard
          label="Open Positions"
          value={stats.openPositions}
          icon={Briefcase}
          trend="neutral"
        />
      </div>

      {/* Two Column Layout */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent Analyses */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Recent Analyses</CardTitle>
              <Link href="/analyses" className="text-sm text-primary hover:underline flex items-center gap-1">
                View all
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {dashboard?.recentAnalyses && dashboard.recentAnalyses.length > 0 ? (
              <div className="space-y-4">
                {dashboard.recentAnalyses.slice(0, 5).map((analysis) => (
                  <Link
                    key={analysis.id}
                    href={`/markets/${analysis.marketId}`}
                    className="block p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <PlatformBadge platform={analysis.platform} />
                        <StatusBadge status={analysis.status} />
                        {analysis.status === 'complete' && analysis.recommendation && (
                          <RecommendationBadge 
                            recommendation={analysis.recommendation as 'YES' | 'NO'} 
                            size="sm" 
                          />
                        )}
                      </div>
                    </div>
                    <p className="text-sm font-medium line-clamp-2">
                      {analysis.marketQuestion}
                    </p>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p>No analyses yet</p>
                <Link href="/markets" className="text-sm text-primary hover:underline mt-2 inline-block">
                  Analyze your first market
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Picks */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Top AI Picks</CardTitle>
              <Link href="/picks" className="text-sm text-primary hover:underline flex items-center gap-1">
                View all
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {dashboard?.topPicks && dashboard.topPicks.length > 0 ? (
              <div className="space-y-4">
                {dashboard.topPicks.slice(0, 3).map((pick, idx) => (
                  <div key={idx} className="p-4 rounded-lg border border-border">
                    <div className="flex items-start justify-between mb-3">
                      <PlatformBadge platform={pick.platform} />
                      <RecommendationBadge 
                        recommendation={pick.recommendation as 'YES' | 'NO'} 
                        size="sm" 
                      />
                    </div>
                    <p className="text-sm font-medium mb-3 line-clamp-2">
                      {pick.marketQuestion}
                    </p>
                    <ConfidenceBar confidence={pick.confidence} showLabel={false} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p>No picks available</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Active Positions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Active Positions</CardTitle>
            <Link href="/portfolio" className="text-sm text-primary hover:underline flex items-center gap-1">
              View all
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          {dashboard?.activePositions && dashboard.activePositions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border text-left text-sm text-muted-foreground">
                    <th className="pb-3 font-medium">Market</th>
                    <th className="pb-3 font-medium">Side</th>
                    <th className="pb-3 font-medium">Entry</th>
                    <th className="pb-3 font-medium text-right">P&L</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {dashboard.activePositions.slice(0, 5).map((position) => {
                    const pnl = position.pnl || 0;
                    return (
                      <tr key={position.id} className="border-b border-border last:border-0">
                        <td className="py-3 max-w-xs truncate">{position.marketQuestion}</td>
                        <td className="py-3">
                          <span className={cn(
                            'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                            position.side === 'YES' ? 'badge-yes' : 'badge-no'
                          )}>
                            {position.side}
                          </span>
                        </td>
                        <td className="py-3">{position.entryPrice}¢</td>
                        <td className={cn(
                          'py-3 text-right font-semibold',
                          pnl >= 0 ? 'text-emerald-600' : 'text-red-600'
                        )}>
                          {pnl >= 0 ? '+' : ''}${pnl.toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <p>No active positions</p>
              <Link href="/portfolio" className="text-sm text-primary hover:underline mt-2 inline-block">
                Add your first position
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
